export interface PlottedDot {
  id: string
  x: number
  y: number
  radius: number
}

export interface DotOffset {
  dx: number
  dy: number
}

export interface TieFan {
  point: { x: number; y: number }
  ids: readonly string[]
  offsets: ReadonlyMap<string, DotOffset>
  bounds: { left: number; right: number; height: number }
}

const FAN_GAP = 3

export function pointKey(x: number, y: number): string {
  return `${String(x)},${String(y)}`
}

export function tieFans(dots: readonly PlottedDot[]): Map<string, TieFan> {
  const byPoint = new Map<string, PlottedDot[]>()

  for (const dot of dots) {
    const key = pointKey(dot.x, dot.y)
    const group = byPoint.get(key)

    if (group) group.push(dot)
    else byPoint.set(key, [dot])
  }

  const fans = new Map<string, TieFan>()

  for (const [key, group] of byPoint) {
    const [anchor, ...rest] = [...group].sort(
      (a, b) => b.radius - a.radius || a.id.localeCompare(b.id),
    )

    if (!anchor || rest.length === 0) continue

    fans.set(key, fanOut(anchor, rest))
  }

  return fans
}

function fanOut(anchor: PlottedDot, rest: readonly PlottedDot[]): TieFan {
  const offsets = new Map<string, DotOffset>([[anchor.id, { dx: 0, dy: 0 }]])

  let right = anchor.radius
  let left = anchor.radius
  let tallest = anchor.radius

  rest.forEach((dot, index) => {
    if (index % 2 === 0) {
      offsets.set(dot.id, { dx: right + FAN_GAP + dot.radius, dy: 0 })
      right += FAN_GAP + dot.radius * 2
    } else {
      offsets.set(dot.id, { dx: -(left + FAN_GAP + dot.radius), dy: 0 })
      left += FAN_GAP + dot.radius * 2
    }

    tallest = Math.max(tallest, dot.radius)
  })

  return {
    point: { x: anchor.x, y: anchor.y },
    ids: [anchor.id, ...rest.map((dot) => dot.id)],
    offsets,
    bounds: { left, right, height: tallest * 2 },
  }
}
